import { createFileRoute, Link } from "@tanstack/react-router";
import { getNews, getPressReleases, getFeatures, NEWSROOM_DISCLOSURE } from "@/lib/news";
import { OFFICIAL_PROFILES, PRESS_KIT_URL } from "@/lib/press";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { breadcrumbs, itemList, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Media & Interviews | Buddha KA Official Newsroom Index";
const DESCRIPTION =
  "Every first-party statement, press release, and feature published by the Buddha KA / KAfrequency newsroom, plus the official press kit and verified profiles.";

export const Route = createFileRoute("/media/")({
  component: MediaIndex,
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/media" }),
    links: [{ rel: "canonical", href: absoluteUrl("/media") }],
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: `${SITE_URL}/media#media`,
          url: `${SITE_URL}/media`,
          name: "Media & Interviews",
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: {
            publisher: orgRef(),
            mainEntity: itemList(getNews().map((n) => ({ path: `/news/${n.slug}`, name: n.title }))),
          },
        }),
      ),
      ldScript(breadcrumbs([["Media & Interviews", "/media"]])),
    ],
  }),
});

function Section({ title, items }: { title: string; items: ReturnType<typeof getNews> }) {
  if (!items.length) return null;
  return (
    <section className="mt-14" aria-labelledby={title}>
      <h2 id={title} className="font-serif text-2xl text-gold-luminous">{title}</h2>
      <ul className="mt-6 divide-y divide-border/60">
        {items.map((n) => (
          <li key={n.slug} className="py-5">
            <Link to="/news/$slug" params={{ slug: n.slug }} className="font-serif text-xl hover:text-gold-luminous">
              {n.title}
            </Link>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {n.publishedAt} · {n.category}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{n.dek}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MediaIndex() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Newsroom Index</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl">Media &amp; Interviews</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">{DESCRIPTION}</p>
      <p className="mt-4 max-w-2xl rounded-xl border hairline bg-charcoal/40 p-4 text-sm text-muted-foreground">
        {NEWSROOM_DISCLOSURE} Independent coverage is not listed here unless and until it is verified and recorded.
      </p>

      <Section title="Press releases" items={getPressReleases()} />
      <Section title="Features & statements" items={getFeatures()} />

      <section className="mt-14" aria-labelledby="press-resources">
        <h2 id="press-resources" className="font-serif text-2xl text-gold-luminous">For press</h2>
        <ul className="mt-4 space-y-2 text-sm">
          <li>
            <a href={PRESS_KIT_URL} className="text-gold hover:text-gold-luminous">Official press kit</a>
          </li>
          {OFFICIAL_PROFILES.map((p) => (
            <li key={p.platform}>
              <a href={p.url} rel="noopener me" className="text-gold hover:text-gold-luminous">
                {p.platform}
              </a>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
